import { Stripe, StripeElements } from '@stripe/stripe-js';
import { match, P } from 'ts-pattern';
import env from '~/env';
import { logError } from '~/utils/observability';

type State = {
  clientSecret?: string;
  stripeInstance: Stripe | null;
  elements?: StripeElements;
};

const initialState: State = {
  stripeInstance: null,
};

let state: State = { ...initialState };

type ListenerFunction = () => void;
const listeners: Set<ListenerFunction> = new Set();
const notifyChange = () => listeners.forEach((l) => l());

const updateState = (updates: Partial<State>) => {
  state = { ...state, ...updates };

  notifyChange();
};

const resetState = () => {
  state = { ...initialState };

  notifyChange();
};

const createStripe = (stripeAccount: string) => {
  if (!window.Stripe) {
    const error = new Error('Stripe has not been loaded');
    logError(error);
    throw error;
  }

  return window.Stripe(String(env.VITE_STRIPE_PUBLISHABLE_KEY), {
    stripeAccount,
  });
};

const Payments = {
  initializePayment: (clientSecret: string, stripeIdFromPlatform: string) => {
    updateState({
      clientSecret,
      stripeInstance:
        state.stripeInstance ?? createStripe(stripeIdFromPlatform),
    });
  },

  setElements: (elements: StripeElements) => {
    if (elements !== state.elements) {
      updateState({ elements });
    }
  },

  confirmPayment: async () => {
    if (!state.clientSecret || !state.stripeInstance) {
      const error = new Error('Payment has not been created', {
        cause: {
          secret: !!state.clientSecret,
          stripe: !!state.stripeInstance,
        },
      });

      logError(error);
      throw error;
    }

    const card = state.elements?.getElement('card');

    if (!card) {
      const error = new Error('Card element has not been set up');
      logError(error);
      throw error;
    }

    const result = await state.stripeInstance.confirmCardPayment(
      state.clientSecret,
      {
        payment_method: { card },
      },
    );

    return match(result)
      .with({ paymentIntent: P.nonNullable }, ({ paymentIntent }) => {
        resetState();
        return paymentIntent.id;
      })
      .with({ error: P.nonNullable }, ({ error: cause }) => {
        const stripeError = new Error(
          cause.message ??
            'Unable to confirm payment. Please check your payment details and try again.',
          { cause },
        );
        throw stripeError;
      })
      .exhaustive();
  },

  subscribe: (listener: ListenerFunction) => {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot: () => state,

  reset: () => {
    resetState();
  },
};

export default Payments;
